import { activeThreadIdsAtom } from "@/components/threads/hooks/useActiveThreadTracking.ts";
import { isSharedThreadPath } from "@/lib/sharedThread.ts";
import type {
  PresenceClientType,
  PresenceUpdateRequest,
  PresenceUpdateResponse,
  PresenceVisibility,
} from "@/types/presence.ts";
import { useMutation } from "@tanstack/react-query";
import { api } from "@/lib/http.ts";
import { useAtomValue } from "jotai";
import { atomWithStorage, createJSONStorage } from "jotai/utils";
import * as ld from "lodash-es";
import {
  useEffect, // @ts-types="react"
  useEffectEvent,
  useState,
} from "react";

const HEARTBEAT_INTERVAL_MS = 30_000; // 30 seconds
const IDLE_TIMEOUT_MS = 2 * 60 * 1000; // 2 minutes

/**
 * Store a clientId in session storage, to remain stable across e.g. page refreshes.
 *
 * Note that this is distinct from the auth session id stored in the JWT, which is potentially
 * shared across many tabs.
 */
export const clientIdAtom = atomWithStorage<string>(
  "okthx_client_id",
  crypto.randomUUID(),
  createJSONStorage<string>(() => sessionStorage),
  {
    getOnInit: true,
  },
);

/**
 * Hook to detect user idle state based on activity events.
 *
 * Tracks mouse movements, keyboard input, clicks, scrolls, and touch events.
 * Returns a boolean indicating if the user is currently idle.
 */
const useIsIdle = (timeoutMs: number = IDLE_TIMEOUT_MS): boolean => {
  const [isIdle, setIsIdle] = useState(false);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    const handleActivity = ld.debounce(
      () => {
        setIsIdle(false);
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          setIsIdle(true);
        }, timeoutMs);
      },
      20, // short debounce to avoid excessive updates while the user is active
    );

    // Initialize the timeout
    handleActivity();

    // Activity events to listen for
    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];

    // Add event listeners
    for (const event of events) {
      window.addEventListener(event, handleActivity, { passive: true });
    }

    // Cleanup
    return () => {
      clearTimeout(timeoutId);
      for (const event of events) {
        window.removeEventListener(event, handleActivity);
      }
    };
  }, [timeoutMs]);

  return isIdle;
};

/**
 * To be considered "visible" the page must be visible and the user must not be idle
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Document/visibilityState
 */
const getEffectiveVisibility = (isIdle: boolean): PresenceVisibility => {
  return document.visibilityState === "visible" && !isIdle ? "visible" : "hidden";
};

/**
 * Hook to track user presence on the server.
 *
 * Sends updates to the server:
 * - When active thread changes
 * - When page becomes hidden/visible
 * - Every 30 seconds: Heartbeat with current state
 *
 * The server updates the client's thread list based on visibility,
 * providing realtime presence tracking without needing to track diffs.
 * @see getEffectiveVisibility
 *
 * This hook should be mounted once at the app level.
 */
export const usePresenceTracking = () => {
  const activeThreadIds = useAtomValue(activeThreadIdsAtom);
  const clientId = useAtomValue(clientIdAtom);
  const isIdle = useIsIdle();
  // The web SPA only ever runs as a standalone browser now — it's no longer
  // embedded in the native app's webview — so it always reports as "web".
  const clientType: PresenceClientType = "web";

  const presenceMutation = useMutation({
    mutationFn: async (data: {
      threadIds: Set<number>;
      clientId: string;
      visibility: PresenceVisibility;
    }) => {
      const body: PresenceUpdateRequest = {
        clientId,
        clientType,
        threadIds: Array.from(data.threadIds),
        visibility: data.visibility,
      };
      const response = await api.post<PresenceUpdateResponse>("/api/a/presence", body);
      return response.data;
    },
  });

  // Send update when:
  // - active thread set changes
  // - page becomes idle or active
  // don't use the effectEvent, we want to trigger this when these values change
  useEffect(() => {
    // Skip presence tracking for shared thread views (unauthenticated users)
    if (isSharedThreadPath()) return;

    presenceMutation.mutate({
      clientId,
      threadIds: activeThreadIds,
      visibility: getEffectiveVisibility(isIdle),
    });
  }, [activeThreadIds, isIdle, presenceMutation.mutate, clientId]);

  // effectEvent so we can avoid resetting listeners/intervals in the effect below
  // when these values change
  const presenceMutationEffectEvent = useEffectEvent(() => {
    // Skip presence tracking for shared thread views (unauthenticated users)
    if (isSharedThreadPath()) return;

    presenceMutation.mutate({
      clientId,
      threadIds: activeThreadIds,
      visibility: getEffectiveVisibility(isIdle),
    });
  });

  // Setup periodic heartbeat and visibility handling. Send update:
  // - on a fixed interval (while visible and not idle)
  // - when the page becomes visible or hidden
  useEffect(() => {
    const handleVisibilityChange = () => {
      presenceMutationEffectEvent();
    };

    // Setup visibility listener
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Set an interval for periodic heartbeat
    const heartbeatInterval = setInterval(() => {
      presenceMutationEffectEvent();
    }, HEARTBEAT_INTERVAL_MS);

    // Cleanup
    return () => {
      clearInterval(heartbeatInterval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);
};
